import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ApolloWrapper } from '@/components/providers/apollo-provider'
import { AuthProvider, useAuth } from '@/components/providers/auth-provider'
import { ChatActivityProvider } from '@/components/providers/chat-activity-provider'
import { RadarActivityProvider } from '@/components/providers/radar-activity-provider'
import { ThemeProvider } from '@/components/theme-provider'
import { Toaster } from '@/components/ui/toaster'
import { ProtectedRoute } from '@/components/protected-route'
import { GuestBar, GuestSignInDialog } from '@/components/guest/guest-sign-in'
import { AnalyticsTracker } from '@/components/analytics-tracker'
import { PWAInstallPrompt } from '@/components/pwa-install-prompt'
import { ConnectionStatus } from '@/components/connection-status'
import { AppSidebar } from '@/components/app-sidebar'
import { AppHeader } from '@/components/app-header'
import { AgentIdentityStrip } from '@/components/agent-identity-strip'

// Landing page loads synchronously (entry point)
import LandingPage from '@/app/page'
import { LoadingSpinner } from '@/components/loading-spinner'
import { cn } from '@/lib/utils'

// Lazy load all other routes
const OnboardingPage = lazy(() => import('@/app/onboarding/page'))
const AuthCallbackPage = lazy(() => import('@/app/auth/callback/page'))
const SuccessPage = lazy(() => import('@/app/success/page'))
const QueuePage = lazy(() => import('@/app/queue/page'))
const DraftsPage = lazy(() => import('@/app/drafts/page'))
const SettingsPage = lazy(() => import('@/app/settings/page'))
const AnglePage = lazy(() => import('@/app/angle/page'))
const InspirationPage = lazy(() => import('@/app/inspiration/page'))
const CirclePage = lazy(() => import('@/app/circle/page'))
const ChatPage = lazy(() => import('@/app/chat/page'))
const ViralityPage = lazy(() => import('@/app/virality/page'))
const AnalyticsPage = lazy(() => import('@/app/analytics/page'))
const SubmitTestimonialPage = lazy(() => import('@/pages/submit-testimonial'))

// Routes that should show the sidebar when authenticated
const sidebarRoutes = ['/analytics', '/queue', '/inspiration', '/circle', '/angle', '/settings', '/autoscheduler', '/profile', '/scheduler', '/drafts', '/styles', '/chat', '/virality']

const publicRoutes = ['/virality']

function AppContent() {
  const location = useLocation()
  const { isAuthenticated } = useAuth()

  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route))
  const showSidebar = isAuthenticated && sidebarRoutes.some(route => location.pathname.startsWith(route))
  const showHeader = !isAuthenticated && isPublicRoute
  const isChatRoute = showSidebar && location.pathname.startsWith('/chat')

  return (
    <div className="min-h-screen bg-background">
      {showSidebar && <AppSidebar />}
      <GuestSignInDialog />
      {showHeader && <AppHeader />}
      <main
        className={cn(
          // Beside the sidebar the page starts where the sidebar ends: .app-main reads its live width
          // (styles/sidebar.css). On phones the 73px header sits on top instead.
          showSidebar ? 'app-main md:pt-0 pt-[73px]' : showHeader ? 'pt-[73px]' : '',
          // The chat is one screen: the bars take their height, the chat the rest.
          isChatRoute && 'flex h-[100dvh] flex-col overflow-hidden',
        )}
      >
        {showSidebar && <GuestBar />}
        {showSidebar && <AgentIdentityStrip />}
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/dashboard" element={<Navigate to="/analytics" replace />} />
            <Route path="/profile" element={<Navigate to="/settings?tab=profile" replace />} />
            <Route path="/scheduler" element={<Navigate to="/settings?tab=calls" replace />} />
            <Route path="/queue" element={<ProtectedRoute><QueuePage /></ProtectedRoute>} />
            {/* Slop Cannon keeps the /queue address; the old /newsroom link still works. */}
            <Route path="/newsroom" element={<Navigate to="/queue" replace />} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/angle" element={<ProtectedRoute><AnglePage /></ProtectedRoute>} />
            <Route path="/inspiration" element={<ProtectedRoute><InspirationPage /></ProtectedRoute>} />
            <Route path="/circle" element={<ProtectedRoute><CirclePage /></ProtectedRoute>} />
            {/* Old IA — everything below redirects into the four-surface nav */}
            <Route path="/autoscheduler" element={<Navigate to="/queue" replace />} />
            <Route path="/drafts" element={<ProtectedRoute><DraftsPage /></ProtectedRoute>} />
            <Route path="/styles" element={<Navigate to="/angle" replace />} />
            <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/virality" element={<ViralityPage />} />
            <Route path="/analytics" element={<ProtectedRoute><AnalyticsPage /></ProtectedRoute>} />
            <Route path="/submit-testimonial" element={<SubmitTestimonialPage />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  )
}

function App() {
  // Light-only. The old theme selector left "Midnight Luxe" CSS variables
  // inline on the root element and a theme-variant key behind; clear both so
  // returning visitors don't boot into half-dark leftovers.
  useEffect(() => {
    document.documentElement.removeAttribute('style')
    document.documentElement.classList.remove('dark')
    localStorage.removeItem('theme-variant')
    localStorage.setItem('theme', 'light')
  }, [])

  return (
    <ApolloWrapper>
      <AuthProvider>
        <ThemeProvider
          attribute="class"
          forcedTheme="light"
          disableTransitionOnChange
        >
          <AnalyticsTracker />
          <ChatActivityProvider>
            <RadarActivityProvider>
              <AppContent />
            </RadarActivityProvider>
          </ChatActivityProvider>
          <Toaster />
          <PWAInstallPrompt />
          <ConnectionStatus />
        </ThemeProvider>
      </AuthProvider>
    </ApolloWrapper>
  )
}

export default App
